import { useRef, useState } from 'react';
import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMicrophone, IconPlayerStopFilled, IconX } from '@tabler/icons-react';

/**
 * Pick a MediaRecorder mime the browser supports. Messenger prefers mp4/m4a
 * audio; Chrome typically only records webm/opus, which we name accordingly so
 * the server's extension-from-filename fallback still tags the upload.
 */
function pickMime(): { mime: string; ext: string } | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates: Array<{ mime: string; ext: string }> = [
    { mime: 'audio/mp4', ext: 'm4a' },
    { mime: 'audio/aac', ext: 'aac' },
    { mime: 'audio/mpeg', ext: 'mp3' },
    { mime: 'audio/webm', ext: 'webm' },
  ];
  return candidates.find((c) => MediaRecorder.isTypeSupported(c.mime)) ?? null;
}

/** Composer slot: record a voice note and send it as an audio attachment. */
export function VoiceNoteButton({
  onSend,
  disabled,
}: {
  onSend?: (file: File) => void;
  disabled: boolean;
}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelRef = useRef(false);

  function cleanup() {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    recorderRef.current = null;
    chunksRef.current = [];
    setRecording(false);
    setSeconds(0);
  }

  async function start() {
    const picked = pickMime();
    if (!picked) {
      notifications.show({
        message: 'Voice recording is not supported in this browser.',
        color: 'red',
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: picked.mime });
      chunksRef.current = [];
      cancelRef.current = false;
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: picked.mime });
        const cancelled = cancelRef.current;
        cleanup();
        if (!cancelled && blob.size > 0) {
          onSend?.(new File([blob], `voice-note.${picked.ext}`, { type: picked.mime }));
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      notifications.show({ message: 'Microphone permission denied.', color: 'red' });
      cleanup();
    }
  }

  function stop(cancel: boolean) {
    cancelRef.current = cancel;
    recorderRef.current?.stop();
  }

  if (recording) {
    const mm = Math.floor(seconds / 60);
    const ss = String(seconds % 60).padStart(2, '0');
    return (
      <Group gap={4} wrap="nowrap">
        <Tooltip label="Cancel">
          <ActionIcon size="lg" variant="subtle" color="gray" onClick={() => stop(true)}>
            <IconX size={18} />
          </ActionIcon>
        </Tooltip>
        <Text size="xs" c="red" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
          ● {mm}:{ss}
        </Text>
        <Tooltip label="Stop & send">
          <ActionIcon size="lg" variant="filled" color="red" onClick={() => stop(false)}>
            <IconPlayerStopFilled size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    );
  }

  return (
    <Tooltip label="Record voice note">
      <ActionIcon size="lg" variant="subtle" color="gray" disabled={disabled} onClick={start}>
        <IconMicrophone size={18} />
      </ActionIcon>
    </Tooltip>
  );
}
